/**
 * How the person record renders numbers and instants (D580): money as integer minor units + ISO 4217 through `@vello/format`,
 * instants in the scope's market zone (`useScopeZone`, A-18 — an unknown zone prints UTC WITH the marker), a share as a percent and
 * a rating in hundredths. One place, so every card of the record agrees.
 */
import { useMemo } from 'react';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { formatMarketInstant, type InstantStyle } from '../../src/index';
import { useScopeZone } from './scope-zone';

export interface MoneyLike {
  readonly amountMinor: string;
  readonly currency: string;
}

export interface PersonFormat {
  readonly lang: string;
  readonly money: (m: MoneyLike | null | undefined) => string;
  readonly when: (iso: string | null | undefined, style?: InstantStyle) => string;
  readonly day: (iso: string | null | undefined) => string;
  readonly percent: (ratio: number) => string;
  readonly rating: (hundredths: number | null | undefined) => string;
  readonly count: (n: number) => string;
}

export function usePersonFormat(): PersonFormat {
  const { zone, lang } = useScopeZone();
  return useMemo<PersonFormat>(
    () => ({
      lang,
      money: (m) =>
        m === null || m === undefined
          ? '—'
          : /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency)
            ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })
            : `${m.amountMinor} ${m.currency}`,
      when: (iso, style = 'datetime') => formatMarketInstant(iso ?? undefined, zone, lang, style),
      day: (iso) => formatMarketInstant(iso ?? undefined, zone, lang, 'date-short'),
      percent: (ratio) => new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 }).format(ratio),
      rating: (hundredths) => (hundredths === null || hundredths === undefined ? '—' : new Intl.NumberFormat(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hundredths / 100)),
      count: (n) => new Intl.NumberFormat(lang).format(n),
    }),
    [zone, lang],
  );
}

/** Sum money per currency (a currency is never added to another); a currency with no amount is left out. */
export function sumByCurrency(parts: readonly (MoneyLike | null | undefined)[]): readonly MoneyLike[] {
  const totals = new Map<string, bigint>();
  for (const p of parts) {
    if (p === null || p === undefined || !/^-?\d+$/.test(p.amountMinor)) continue;
    totals.set(p.currency, (totals.get(p.currency) ?? 0n) + BigInt(p.amountMinor));
  }
  return [...totals].map(([currency, amount]) => ({ amountMinor: amount.toString(), currency }));
}

/** The mean of an amount over a count, per currency, rounded half up in minor units. */
export function averageByCurrency(parts: readonly { readonly total: MoneyLike | null | undefined; readonly count: number }[]): readonly MoneyLike[] {
  const sums = new Map<string, { sum: bigint; count: bigint }>();
  for (const p of parts) {
    if (p.total === null || p.total === undefined || !/^-?\d+$/.test(p.total.amountMinor) || p.count <= 0) continue;
    const cur = sums.get(p.total.currency) ?? { sum: 0n, count: 0n };
    sums.set(p.total.currency, { sum: cur.sum + BigInt(p.total.amountMinor), count: cur.count + BigInt(p.count) });
  }
  return [...sums].map(([currency, { sum, count }]) => ({ amountMinor: ((sum * 2n + count) / (count * 2n)).toString(), currency }));
}
